import { FC, useEffect, useState } from "react";
import { StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import CommonLayout from "./CommonLayout";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentHolidayPackage, setHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import { useCreateHolidayPackageMutation, useLazyGetHolidayPackageQuery } from "application/features/holidayPackage/holidayPackageApiSlice";
import { useTranslation } from "react-i18next";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import type { HolidayPackage } from "data/types/holidayPackage";

export interface AddHolidayPackage9Props { }

const PageAddHolidayPackage9: FC<AddHolidayPackage9Props> = () => {

    const { t, ready } = useTranslation("translation");

    const holidayPackage = useSelector(selectCurrentHolidayPackage);
    const [holidayPackageCard, setHolidayPackageCard] = useState<Partial<HolidayPackage>>({ package_uuid: "" });
    const [createHolidayPackage, { isLoading, isError }] = useCreateHolidayPackageMutation();
    const [triggerGetQuery, { data: getHolidayPackage, isLoading: isLoadingHolidayPackage, isSuccess }] = useLazyGetHolidayPackageQuery();
    const dispatch = useDispatch();

    const handleCreateHolidayPackage = async (): Promise<void> => {
        try {
            const obj = {
                ...holidayPackage,
            };

            console.log("Creating holiday package with data:", obj);

            const response = await createHolidayPackage(obj).unwrap();
            const updatedHolidayPackage = await triggerGetQuery(response.package_uuid).unwrap();

            // Ensure the holiday package data exists before setting it
            if (updatedHolidayPackage?.holidayPackage) {
                setHolidayPackageCard(updatedHolidayPackage.holidayPackage);
            } else {
                console.error("Holiday package data is missing from API response");
                setHolidayPackageCard({ package_uuid: response.package_uuid || "" });
            }

            // Reset the experience in the Redux store
            dispatch(setHolidayPackage({}));
        } catch (err) {
            console.error("Error creating holiday package:", err);
            // Set a fallback state to prevent UI crashes
            setHolidayPackageCard({ package_uuid: "" });
        }
    };

    useEffect(() => {
        if (holidayPackage && Object.keys(holidayPackage).length > 0) {
            handleCreateHolidayPackage();
        }
    }, [holidayPackage]);

    if (isError) {
        return (
            <div className="flex flex-1 items-center justify-center p-10 w-screen h-screen">
                <div className="md:w-1/3 w-full">
                    <h2 className="text-2xl font-semibold">Errore durante la creazione dell'esperienza</h2>
                    <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
                        Si è verificato un errore durante la creazione della tua esperienza. Riprova più tardi.
                    </span>
                </div>
            </div>
        )
    }

    if (!ready) return null;

    return (
        <CommonLayout
            index="09"
            backtHref={undefined}
            nextHref={"/host/my-properties"}
            canGoNext={true}
            widePage={false}
        >

            {
                (isLoading || isLoadingHolidayPackage) && (
                    <div className="flex flex-1 items-center justify-center p-10 w-screen h-screen">
                        <div className="md:w-1/3 w-full">
                            <Skeleton className="w-full h-[30rem] md:h-[40rem]" />
                        </div>
                    </div>
                )
            }

            {
                (isSuccess && holidayPackageCard?.package_uuid && holidayPackageCard.package_uuid !== "") && (
                    <>
                        <div className="max-w-2xl mx-auto">
                            <h2 className="text-2xl font-semibold">{t("translation:components.holidayPackageForm.page9.title")}</h2>
                            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
                            <div className="space-y-8">
                                <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("translation:components.holidayPackageForm.page9.description")}</p>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold">{t("translation:components.holidayPackageForm.page9.yourListing")}</h3>
                            <div className="max-w-xs">
                                <MultiStockCard
                                    id={holidayPackageCard.package_uuid}
                                    className="mt-8"
                                    cover={holidayPackageCard.cover_image}
                                    images={holidayPackageCard.gallery_images || []}
                                    title={holidayPackageCard.name}
                                    description={holidayPackageCard.description || ""}
                                    address={holidayPackageCard.mood || ""}
                                    price={holidayPackageCard.price}
                                    rating={null}
                                    reviewCount={null}
                                    href={stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, holidayPackageCard.slug || holidayPackageCard.package_uuid)}
                                />
                            </div>
                            <div className="flex items-center space-x-5 mt-8">
                                <ButtonSecondary href={"/host/edit-holiday-package/" + (holidayPackageCard?.package_uuid || "")}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                    </svg>
                                    <span className="ml-3">{t("translation:components.holidayPackageForm.page9.modifyListing")}</span>
                                </ButtonSecondary>
                            </div>
                        </div>
                    </>
                )
            }
        </CommonLayout >
    );
};

export default PageAddHolidayPackage9;
